// Operator › Deploys › a deploy (`/operator/deploys/$deployId`; DC-04, ADR-0018 §5): the deploy
// with its bundle (`GET /v1/operator/deploys/{id}`, polled while it moves), its hand-off status
// timeline, why it failed, and the bundle's facts (sha, verification, token count, what the
// company's override changed, every file's hash). A first deploy waiting for a person is
// approved or rejected here (`POST …/approve`, `…/reject` with a reason). DC-05: a pilot of a
// version with no verification skill waits for the operator's verification override (reason,
// pilot end; `POST …/verification-override`), which builds its unverified bundle. A confirmed
// deploy shows the DE's drift audits (DC-10) when the company has the `agents` add-on.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Field,
  FieldLabel,
  Input,
  LocationHeader,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckIcon, ShieldAlertIcon, XIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../../api/client";
import type { ConfigurationBundle, Deploy, MeResponse } from "../../../api/types";
import { DriftAuditsSection } from "../../de/drift-audits";
import { UnverifiedBadge } from "../../de/unverified-badge";
import { formatDateTime } from "../../format";
import { OpsDoc, OpsFrame, operatorCrumb } from "../../ops-ux";
import { Facts, LoadError, Loading, Section } from "../../screen-parts";
import { featureState } from "../../use-feature";
import { problemLines, versionLabel } from "../templates/template-parts";
import { agentTemplateQuery } from "../templates/template-queries";
import {
  awaitsApproval,
  awaitsOverride,
  companyName,
  DeployStateBadge,
  DeployTimeline,
  ERROR_HELP,
  KIND_LABEL,
} from "./deploy-parts";
import { deployKeys, deployQuery, deployTargetsQuery } from "./deploy-queries";

export function DeployDetail({
  api,
  me,
  deployId,
}: {
  api: Api;
  me: MeResponse;
  deployId: string;
}) {
  const detail = useQuery(deployQuery(api, deployId));
  const targets = useQuery(deployTargetsQuery(api));
  const templateId = detail.data?.deploy.template_id ?? "";
  const template = useQuery({ ...agentTemplateQuery(api, templateId), enabled: !!templateId });
  const crumbs = [operatorCrumb, { label: "Deploys", render: <Link to="/operator/deploys" /> }];

  if (detail.isPending)
    return (
      <OpsFrame>
        <LocationHeader crumbs={crumbs} title="Deploy" />
        <OpsDoc>
          <Loading label="Loading the deploy" />
        </OpsDoc>
      </OpsFrame>
    );
  if (detail.isError)
    return (
      <OpsFrame>
        <LocationHeader crumbs={crumbs} title="Deploy" />
        <OpsDoc>
          <LoadError what="The deploy" error={detail.error} />
        </OpsDoc>
      </OpsFrame>
    );

  const { deploy: d, bundle, verification_override: pilot } = detail.data;
  const waitsForOverride = awaitsOverride(d);
  const decidable = awaitsApproval(d) && !waitsForOverride;
  const de =
    targets.data?.data.find((r) => r.digital_employee.membership_id === d.de_membership_id)
      ?.digital_employee.display_name ?? `DE …${d.de_membership_id.slice(-6)}`;
  const company = companyName(me, d.target_company_id);
  const versions = template.data?.versions ?? [];
  const version = versionLabel(versions, d.template_version_id);
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={crumbs}
        title={`${de} · ${company}`}
        badge={
          <span className="inline-flex gap-1.5">
            <DeployStateBadge deploy={d} />
            {pilot || waitsForOverride || bundle?.verification_state === "unverified" ? (
              <UnverifiedBadge pilot={pilot} />
            ) : null}
          </span>
        }
        actions={decidable ? <Decide api={api} deploy={d} /> : undefined}
      />
      <OpsDoc>
        <p className="mb-4 text-xs text-muted-foreground" data-slot="deploy-facts">
          {KIND_LABEL[d.kind]} of {template.data?.template.name ?? "a template"} {version} · staged{" "}
          {formatDateTime(d.created_at)}
        </p>
        {waitsForOverride ? (
          <OverrideVerification api={api} me={me} deploy={d} company={company} />
        ) : null}
        {decidable ? (
          <Notice tone="warning" data-slot="awaits-approval">
            This is the first deploy of this version into {company}. A person approves it before it
            is handed off: you, as the company's operator, or its owner.
          </Notice>
        ) : null}
        {d.state === "failed" && d.error ? (
          <Notice tone="danger" role="alert" title="The deploy failed" data-slot="deploy-failure">
            <p>{ERROR_HELP[d.error.code]}</p>
            <p className="mt-1">
              <code className="text-xs">{d.error.code}</code> · {d.error.message}
            </p>
          </Notice>
        ) : null}
        {d.state === "rejected" ? (
          <Notice tone="neutral" title="Rejected" data-slot="deploy-rejected">
            {d.rejected_reason}
          </Notice>
        ) : null}
        <Section
          id="deploy-timeline"
          title="Hand-off status"
          description="Staged, approved, handed off to AiGentPanel, then confirmed or failed."
        >
          <DeployTimeline deploy={d} />
        </Section>
        <Section id="deploy-facts" title="Deploy">
          <Facts
            items={[
              { label: "Company", value: company },
              { label: "Digital employee", value: de },
              {
                label: "Version",
                value: (
                  <Link
                    to="/operator/templates/$templateId/versions/$versionId"
                    params={{ templateId: d.template_id, versionId: d.template_version_id }}
                    className="underline-offset-4 hover:underline"
                  >
                    {template.data ? `${template.data.template.name} ${version}` : "Open version"}
                  </Link>
                ),
              },
              { label: "Kind", value: KIND_LABEL[d.kind] },
              { label: "Staged by", value: stagedBy(d, me) },
              {
                label: "Approved by",
                value: d.approved_at
                  ? d.approved_by_membership_id
                    ? "The company's owner"
                    : d.approved_by_user_id === me.user.id
                      ? "You"
                      : "An agency operator"
                  : "—",
              },
              { label: "Hand-off attempts", value: String(d.handoff_attempts) },
              // AGENT-POLISH: the readable profile name the hand-off sends (stored once deployed).
              {
                label: "AiGentPanel profile",
                value: detail.data.profile_name ? (
                  <code className="text-xs" data-slot="deploy-profile-name">
                    {detail.data.profile_name}
                  </code>
                ) : (
                  "—"
                ),
              },
              {
                label: "Running as",
                value: d.panel_ref ? (
                  <code className="text-xs">
                    {d.panel_ref.profile_name} on {d.panel_ref.node_id}
                  </code>
                ) : (
                  "—"
                ),
              },
              {
                label: "Replaces",
                value: d.previous_deploy_id ? (
                  <Link
                    to="/operator/deploys/$deployId"
                    params={{ deployId: d.previous_deploy_id }}
                    className="underline-offset-4 hover:underline"
                  >
                    The previous confirmed deploy
                  </Link>
                ) : (
                  "Nothing (first of this DE)"
                ),
              },
              { label: "Staged", value: formatDateTime(d.created_at) },
            ]}
          />
        </Section>
        {pilot ? (
          <Section
            id="deploy-override"
            title="Verification override"
            description="This version has no verification skill: it ships as a pilot, shown unverified on the DE's profile and on every approval it raises."
          >
            <Facts
              items={[
                { label: "Reason", value: pilot.reason },
                {
                  label: "Pilot until",
                  value: `${formatDateTime(pilot.pilot_until)}${
                    Date.parse(pilot.pilot_until) <= Date.now() ? " (ended)" : ""
                  }`,
                },
                {
                  label: "Overridden by",
                  value: pilot.operator_user_id === me.user.id ? "You" : "An agency operator",
                },
                { label: "Overridden", value: formatDateTime(pilot.created_at) },
              ]}
            />
          </Section>
        ) : null}
        {bundle ? <BundleSection bundle={bundle} /> : null}
        {/* DC-10: a confirmed deploy is what the DE runs; its audits re-run that version's evals. */}
        {d.state === "confirmed" && featureState(me, d.target_company_id, "agents").enabled ? (
          <div className="mt-8">
            <DriftAuditsSection
              api={api}
              companyId={d.target_company_id}
              deId={d.de_membership_id}
              deployed
            />
          </div>
        ) : null}
      </OpsDoc>
    </OpsFrame>
  );
}

/**
 * DC-05: the operator's override for a pilot of a version with no verification skill (05: an
 * agency operator of the company holding the `verification_override` relation; a reason and the
 * pilot's end are required). Without the relation, the screen says who can.
 */
function OverrideVerification({
  api,
  me,
  deploy,
  company,
}: {
  api: Api;
  me: MeResponse;
  deploy: Deploy;
  company: string;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const reasonId = useId();
  const untilId = useId();
  const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.deploys.overrideVerification({
        params: { deploy_id: deploy.id },
        idempotencyKey: newIdempotencyKey(),
        body: { reason: reason.trim(), pilot_until: `${until}T23:59:59.000Z` },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: deployKeys.all() }),
  });
  if (!me.agency?.verification_override)
    return (
      <Notice
        tone="warning"
        title="Waiting for a verification override"
        data-slot="awaits-override"
      >
        This version has no verification skill, so it deploys into {company} only as a pilot. An
        agency operator with the verification override relation records the reason and when the
        pilot ends; then a person approves it.
      </Notice>
    );
  return (
    <Notice tone="warning" title="Override verification for a pilot" data-slot="awaits-override">
      <p>
        This version has no verification skill. Overriding builds its bundle as{" "}
        <strong>unverified</strong>: the DE shows the unverified badge on its profile and on every
        approval it raises, until a verified version replaces it. Pilots only; the override is
        logged to the platform audit.
      </p>
      <form
        className="mt-3 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field>
          <FieldLabel htmlFor={reasonId}>Reason</FieldLabel>
          <Textarea
            id={reasonId}
            rows={2}
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={untilId}>Pilot ends</FieldLabel>
          <Input
            id={untilId}
            type="date"
            min={tomorrow}
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="max-w-48"
          />
        </Field>
        {save.isError ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemLines(save.error, "The override could not be recorded.").join(" · ")}
          </p>
        ) : null}
        <div>
          <Button type="submit" disabled={save.isPending || !reason.trim() || !until}>
            <ShieldAlertIcon aria-hidden="true" />
            {save.isPending ? "Overriding…" : "Override verification"}
          </Button>
        </div>
      </form>
    </Notice>
  );
}

function stagedBy(d: Deploy, me: MeResponse): string {
  const s = d.staged_by;
  if (s.kind === "user") return s.user_id === me.user.id ? "You" : "An agency operator";
  if (s.kind === "run") return "The Deployment engineer";
  return "A company member";
}

type Report = {
  skill_index_token_cap?: number;
  overridden_fields?: string[];
  model_map_checked_on_gateway?: boolean;
  lead_model?: string | null;
  files?: { path: string; sha256: string }[];
};

function BundleSection({ bundle }: { bundle: ConfigurationBundle }) {
  const r = bundle.validation_report as Report;
  const files = Array.isArray(r.files) ? r.files : [];
  const overridden = Array.isArray(r.overridden_fields) ? r.overridden_fields : [];
  return (
    <Section
      id="deploy-bundle"
      title="Configuration bundle"
      description="What AiGentPanel receives; it recomputes the sha from the files and refuses a mismatch."
    >
      <Facts
        items={[
          {
            label: "Bundle sha",
            value: <code className="break-all text-xs">{bundle.bundle_sha}</code>,
          },
          {
            label: "Verification",
            value: bundle.verification_state === "verified" ? "Verified" : "Unverified",
          },
          {
            label: "Skill index",
            value: `${bundle.skill_index_tokens} tokens${
              r.skill_index_token_cap ? ` of ${r.skill_index_token_cap}` : ""
            }`,
          },
          {
            label: "Company override",
            value: overridden.length ? overridden.join(", ") : "None",
          },
          {
            label: "Lead model",
            value: r.lead_model
              ? `${r.lead_model}${
                  r.model_map_checked_on_gateway === false ? " (gateway not checked)" : ""
                }`
              : "—",
          },
          { label: "Built", value: formatDateTime(bundle.created_at) },
        ]}
      />
      {files.length ? (
        <Table data-slot="bundle-files">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">File</TableHead>
              <TableHead scope="col">sha256</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {files.map((f) => (
              <TableRow key={f.path}>
                <TableHead scope="row" className="font-normal">
                  <code className="text-xs">{f.path}</code>
                </TableHead>
                <TableCell>
                  <code className="text-xs text-muted-foreground">{f.sha256.slice(0, 16)}…</code>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </Section>
  );
}

/** Approve, or reject with a reason. */
function Decide({ api, deploy }: { api: Api; deploy: Deploy }) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const done = () => qc.invalidateQueries({ queryKey: deployKeys.all() });
  const approve = useMutation({
    mutationFn: () =>
      api.client.operator.deploys.approve({
        params: { deploy_id: deploy.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: done,
  });
  const reject = useMutation({
    mutationFn: () =>
      api.client.operator.deploys.reject({
        params: { deploy_id: deploy.id },
        idempotencyKey: newIdempotencyKey(),
        body: { reason: reason.trim() },
      }),
    onSuccess: async () => {
      await done();
      setRejecting(false);
    },
  });
  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex gap-2">
        <Button variant="outline" size="lg" onClick={() => setRejecting(true)}>
          <XIcon aria-hidden="true" />
          Reject
        </Button>
        <Button size="lg" disabled={approve.isPending} onClick={() => approve.mutate()}>
          <CheckIcon aria-hidden="true" />
          {approve.isPending ? "Approving…" : "Approve deploy"}
        </Button>
      </div>
      {approve.isError ? (
        <p role="alert" className="text-sm text-danger-text">
          {problemLines(approve.error, "The deploy could not be approved.").join(" ")}
        </p>
      ) : null}
      <AlertDialog
        open={rejecting}
        onOpenChange={(o) => {
          setRejecting(o);
          if (!o) reject.reset();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this deploy?</AlertDialogTitle>
            <AlertDialogDescription>
              The deploy closes and is never handed off. Stage again to try another version.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Field>
            <FieldLabel htmlFor={reasonId}>Reason</FieldLabel>
            <Textarea
              id={reasonId}
              rows={2}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          {reject.isError ? (
            <Notice tone="danger" role="alert">
              {problemLines(reject.error, "The deploy could not be rejected.").join(" · ")}
            </Notice>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={reject.isPending || !reason.trim()}
              onClick={() => reject.mutate()}
            >
              {reject.isPending ? "Rejecting…" : "Reject deploy"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
